import type { InterviewInviteListItemDto } from '@sara/api-client';
import { InterviewStatusBadge } from '../InterviewStatusBadge';
import { AttemptMeter, EndedByTag } from '../CallAttempts';

type InterviewStatusCellProps = Pick<
  InterviewInviteListItemDto,
  'status' | 'attempts' | 'maxAttempts' | 'endedBy'
>;

/**
 * Status column cell: the status badge anchors a compact meta line carrying the
 * two other things we know about an invite's call history — how many attempts
 * have been spent (2/3) and who ended the surfaced attempt.
 */
export function InterviewStatusCell({
  status,
  attempts,
  maxAttempts,
  endedBy,
}: InterviewStatusCellProps) {
  const showMeter = attempts > 0 && maxAttempts > 0;
  const hasMeta = showMeter || endedBy !== null;
  // Flag the meter only when tries ran out without completing — a completed
  // invite that happened to use its last attempt isn't a problem worth warning.
  const depleted = attempts >= maxAttempts && status !== 'COMPLETED';

  return (
    <div className="flex flex-col items-start gap-1.5">
      <InterviewStatusBadge status={status} />
      {hasMeta && (
        <div className="flex items-center gap-2 whitespace-nowrap pl-0.5 text-[11px] leading-none text-muted-foreground">
          {showMeter && (
            <AttemptMeter attempts={attempts} maxAttempts={maxAttempts} depleted={depleted} />
          )}
          {showMeter && endedBy !== null && (
            <span className="text-border" aria-hidden="true">
              •
            </span>
          )}
          {endedBy !== null && <EndedByTag endedBy={endedBy} />}
        </div>
      )}
    </div>
  );
}
